'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AuthModal } from '@/components/auth/auth-modal';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuth } from '@/components/auth/auth-provider';

/**
 * Pro upsell, shown as a modal from wherever a restricted feature is touched,
 * replacing the standalone /pricing page.
 *
 * Everything the pricing page used to do lives here: the plan comparison, the
 * same /api/stripe/checkout call, and the auth gate in front of it. Mount
 * <UpgradeProvider> once around a tree and call useUpgrade().open() from any
 * descendant.
 */

type PaidPlan = 'weekly' | 'monthly';

const PRO_FEATURES = [
  'Watermark-free downloads',
  'All 160+ receipt templates',
  'HD PNG & PDF exports',
  'Save & reuse unlimited templates',
  'Cancel anytime',
];

interface UpgradeContextValue {
  /** Open the upsell. The argument is accepted for call-site compatibility
   *  but no longer displayed anywhere. */
  open: (reason?: string) => void;
}

const UpgradeContext = createContext<UpgradeContextValue | null>(null);

export function useUpgrade(): UpgradeContextValue {
  const ctx = useContext(UpgradeContext);
  // A no-op fallback keeps a trigger from crashing a tree that has no provider.
  return ctx ?? { open: () => {} };
}

export function UpgradeProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((_reason?: string) => {
    setIsOpen(true);
  }, []);

  const value = useMemo(() => ({ open }), [open]);

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradeModal open={isOpen} onOpenChange={setIsOpen} />
    </UpgradeContext.Provider>
  );
}

export function UpgradeModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { isPaid } = useAuth();
  const { requireAuth, showAuthModal, setShowAuthModal } = useAuthGate();
  const [loadingPlan, setLoadingPlan] = useState<PaidPlan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = async (plan: PaidPlan) => {
    setError(null);
    setLoadingPlan(plan);
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Could not start checkout. Please try again.');
      }
      window.location.href = data.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setLoadingPlan(null);
    }
  };

  const handleUpgrade = (plan: PaidPlan) => {
    if (!requireAuth(() => startCheckout(plan))) return;
    startCheckout(plan);
  };

  return (
    <>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          // Mobile: a full-screen takeover (max-w-none + inset overrides beat
          // the primitive's centered-card defaults); desktop keeps the card.
          className="max-w-none w-full h-dvh rounded-none top-0 left-0 translate-x-0 translate-y-0 flex flex-col justify-center overflow-y-auto p-0 gap-0 sm:block sm:h-auto sm:max-w-[460px] sm:rounded-lg sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:overflow-hidden"
          style={{ fontFamily: 'Roboto, sans-serif' }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 text-left">
            <div
              className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full"
              style={{ backgroundColor: '#E7F5FF' }}
            >
              <Sparkles className="h-5 w-5" style={{ color: '#0068D6' }} />
            </div>
            <DialogTitle style={{ fontSize: '21px', fontWeight: 700, color: '#1a1a2e' }}>
              Go Pro
            </DialogTitle>
            <DialogDescription style={{ fontSize: '14.5px', color: '#6B6B6B', lineHeight: 1.5 }}>
              Everything else stays free.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6">
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
              {PRO_FEATURES.map((f) => (
                <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontSize: '14.5px', color: '#333333', lineHeight: 1.45 }}>
                  <Check className="h-4 w-4 flex-shrink-0" style={{ color: '#7fb434', marginTop: '2px' }} />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="px-6 pb-2 flex flex-col gap-2.5">
            <Button
              onClick={() => handleUpgrade('monthly')}
              disabled={isPaid || loadingPlan !== null}
              className="w-full h-12 font-semibold justify-between px-5 transition-all hover:brightness-105 hover:-translate-y-px hover:shadow-md active:translate-y-0"
              style={{ backgroundColor: '#0068D6', color: '#FFF', fontSize: '15px' }}
            >
              {loadingPlan === 'monthly' ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                <>
                  <span>{isPaid ? "You're on Pro" : 'Go Pro monthly'}</span>
                  {!isPaid && <span style={{ fontWeight: 700 }}>$9 / mo</span>}
                </>
              )}
            </Button>
            <Button
              onClick={() => handleUpgrade('weekly')}
              disabled={isPaid || loadingPlan !== null}
              className="w-full h-11 font-semibold justify-between px-5 transition-all hover:bg-[#d8ecff] hover:-translate-y-px hover:shadow-sm active:translate-y-0"
              style={{ backgroundColor: '#E7F5FF', color: '#0b4f92', border: '1px solid #7fbdf2', fontSize: '14.5px' }}
            >
              {loadingPlan === 'weekly' ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                <>
                  <span>{isPaid ? "You're on Pro" : 'Go Pro weekly'}</span>
                  {!isPaid && <span style={{ fontWeight: 700 }}>$4.50 / wk</span>}
                </>
              )}
            </Button>
          </div>

          {error && (
            <p className="px-6 pt-2" style={{ color: '#DC2626', fontSize: '13.5px', margin: 0 }}>
              {error}
            </p>
          )}

          <p className="px-6 pt-4 pb-2" style={{ color: '#9a8f7d', fontSize: '12px', lineHeight: 1.5, margin: 0 }}>
            Secure payment by Stripe. Renews automatically, cancel anytime - your plan stays
            active until the end of the paid period.
          </p>

          {/* Second grey exit besides the top-right X: a plain text opt-out. */}
          <DialogClose asChild>
            <button
              type="button"
              className="mx-auto mb-5 mt-1 block text-[14px] text-gray-400 underline underline-offset-2 transition-colors hover:text-gray-600"
            >
              No thanks, continue with the watermark
            </button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
}
